'use client';
import React, { forwardRef } from 'react';
import InputArea from './InputArea.mjs';
import { INPUT_AREA_TEXT_ALIGN, INPUT_FORMAT_TYPE } from '../../constants/input.mjs';
import { useInputFocus } from '../../hooks/useInputFocus.mjs';
import { getOnKeyDown, getOnBeforeKeyDownNumberInput, getOnChange } from '../../utils/input.mjs';
import { getNumberString } from '../../utils/string.mjs';

const InputUnit = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    unit,
    maxDecimalDigits: _maxDecimalDigits,
    shape,
    boxSize,
    value = '',
    textAlign = INPUT_AREA_TEXT_ALIGN.LEFT,
    readOnly,
    disabled,
    formatType = INPUT_FORMAT_TYPE.NONE,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    hasError,
    errorMessage,
    guideMessage,
    onClickDeleteButton,
    ...restProps
  } = _ref;
  const {
    isFocused,
    handleFocus,
    handleBlur
  } = useInputFocus({
    onFocus,
    onBlur
  });
  const maxDecimalDigits = Math.max(_maxDecimalDigits, 0);
  const format = formatType === INPUT_FORMAT_TYPE.NONE ? undefined : v => getNumberString(v, {
    addComma: formatType === INPUT_FORMAT_TYPE.COMMA_ADDED_NUMBER,
    maxDecimalDigits
  });
  const handleChange = onChange && getOnChange({
    format,
    onChange: e => {
      onChange({
        value: formatType === INPUT_FORMAT_TYPE.NONE ? e.target.value : getNumberString(e.target.value, {
          addComma: false,
          maxDecimalDigits
        }),
        formattedValue: e.target.value
      }, e);
    }
  });
  const handleKeyDown = formatType === INPUT_FORMAT_TYPE.NONE ? onKeyDown : getOnKeyDown({
    onBeforeKeyDown: getOnBeforeKeyDownNumberInput({
      formatType,
      maxDecimalDigits
    }),
    onKeyDown
  });
  const isEditable = !readOnly && !disabled;
  const isShowDeleteButton = isEditable && !!value;
  const inputMode = (() => {
    if (formatType === INPUT_FORMAT_TYPE.NONE) {
      return 'text';
    }
    return maxDecimalDigits > 0 ? 'decimal' : 'numeric';
  })();
  return /*#__PURE__*/React.createElement(InputArea, {
    type: shape,
    boxSize: boxSize,
    textAlign: textAlign,
    hasError: hasError,
    isFocused: isFocused,
    disabled: disabled,
    errorMessage: errorMessage,
    guideMessage: guideMessage
  }, /*#__PURE__*/React.createElement(InputArea.Input, {
    ref: ref,
    inputMode: inputMode,
    value: !format ? value : format(value),
    readOnly: readOnly,
    disabled: disabled,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...restProps
  }), /*#__PURE__*/React.createElement(InputArea.DeleteButton, {
    isShow: isShowDeleteButton,
    onClick: onClickDeleteButton
  }), /*#__PURE__*/React.createElement(InputArea.SideComponent, {
    type: "unit"
  }, unit));
});
InputUnit.displayName = 'InputUnit';

export { InputUnit as default };
